import PropTypes from 'prop-types'
import { useCallback, useRef, useState } from 'react'
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { Alert, Button } from 'ui/baseComponents'
import parseExemptionCsv from './parseCsv'

const CATEGORY_LABELS = {
  MOBILITY: 'Mobility',
  TREMOR_DYSKINESIA: 'Tremor/Dyskinesia',
  SLEEP: 'Sleep',
}

const REASON_LABELS = {
  MEDICAL: 'Medical',
  PERSONAL: 'Personal',
  NON_RESPONSIVE: 'Non-Responsive',
}

const CsvUploadDialog = ({ open, onClose, onUpload }) => {
  const fileInputRef = useRef(null)
  const [parsed, setParsed] = useState(null)
  const [parseErrors, setParseErrors] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadResults, setUploadResults] = useState(null)

  const resetState = useCallback(() => {
    setParsed(null)
    setParseErrors([])
    setUploading(false)
    setUploadResults(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  const handleClose = useCallback(() => {
    resetState()
    onClose()
  }, [resetState, onClose])

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploadResults(null)
    const reader = new FileReader()
    reader.onload = (event) => {
      const { rows, errors } = parseExemptionCsv(event.target.result)
      setParsed(rows)
      setParseErrors(errors)
    }
    reader.readAsText(file)
  }, [])

  const handleUpload = useCallback(async () => {
    if (!parsed?.length) return
    setUploading(true)

    const results = { succeeded: 0, failed: [], total: parsed.length }

    for (const row of parsed) {
      try {
        await onUpload(row)
        results.succeeded++
      } catch (err) {
        results.failed.push({
          patientId: row.patientId,
          error: err.message,
        })
      }
    }

    setUploading(false)
    setUploadResults(results)
    setParsed(null)
  }, [parsed, onUpload])

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Exemptions CSV</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Upload a CSV file with a header row. Columns are matched by name
          (order does not matter). Extra columns are ignored.
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" component="div" color="text.secondary">
            <strong>Required columns:</strong> Patient ID, Reason (Medical |
            Personal | Non-Responsive)
          </Typography>
          <Typography variant="caption" component="div" color="text.secondary">
            <strong>Category columns (TRUE/FALSE):</strong> Mobility, Sleep,
            Tremor &amp; Dyskinesia &mdash; at least one must be TRUE
          </Typography>
          <Typography variant="caption" component="div" color="text.secondary">
            <strong>Optional columns:</strong> Detail, Status, Project ID
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Button
            type="secondary"
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            Select CSV File
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </Box>

        {parseErrors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Parse warnings ({parseErrors.length}):
            </Typography>
            <Box
              component="ul"
              sx={{ m: 0, pl: 2, maxHeight: 120, overflow: 'auto' }}
            >
              {parseErrors.map((err, i) => (
                <li key={i}>
                  <Typography variant="caption">{err}</Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}

        {uploading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary">
              Uploading exemptions...
            </Typography>
          </Box>
        )}

        {uploadResults && (
          <Alert
            severity={uploadResults.failed.length ? 'warning' : 'success'}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">
              {uploadResults.succeeded} of {uploadResults.total} exemptions
              created successfully.
            </Typography>
            {uploadResults.failed.length > 0 && (
              <Box
                component="ul"
                sx={{ m: 0, pl: 2, maxHeight: 120, overflow: 'auto' }}
              >
                {uploadResults.failed.map((f, i) => (
                  <li key={i}>
                    <Typography variant="caption">
                      {f.patientId}: {f.error}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {parsed?.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preview ({parsed.length} rows)
            </Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Patient ID</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Categories</TableCell>
                    <TableCell>Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsed.map((row) => (
                    <TableRow key={row.patientId}>
                      <TableCell>
                        <Typography variant="caption">
                          {row.patientId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={REASON_LABELS[row.reason] || row.reason}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            flexWrap: 'wrap',
                          }}
                        >
                          {row.categories.map((cat) => (
                            <Chip
                              key={cat}
                              label={CATEGORY_LABELS[cat] || cat}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            maxWidth: 200,
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.detail || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button type="secondary" onClick={handleClose} disabled={uploading}>
          {uploadResults ? 'Close' : 'Cancel'}
        </Button>
        {!uploadResults && (
          <Button
            type="primary"
            onClick={handleUpload}
            disabled={!parsed?.length || uploading}
          >
            {uploading
              ? 'Uploading...'
              : `Upload ${parsed?.length || 0} Exemptions`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

CsvUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
}

export default CsvUploadDialog
